/**
 * Unified Desktop Layout Utilities
 * Consolidates all mathematics for grid calculations, coordinate conversions,
 * and collision detection for both icons and widgets.
 */

import { type WidgetDesktopPosition } from "@/types/widget";

export const ICON_SIZE = {
  width: 90,
  height: 100,
};

// Slightly tighter grid for better visual density
export const GRID_PADDING = {
  x: 2,
  y: 8,
};

export const GRID_CELL_WIDTH = ICON_SIZE.width + GRID_PADDING.x;
export const GRID_CELL_HEIGHT = ICON_SIZE.height + GRID_PADDING.y;

export const EDGE_INSET_RATIO = 0.15;
export const EDGE_INSET_MAX = 24;

export const DESKTOP_AREA_PADDING = 20;

export interface GridDimensions {
  rows: number;
  cols: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Calculates a key for a grid cell
 */
export const makeCellKey = (row: number, col: number) => `${row}:${col}`;

/**
 * Clamps a grid position to the desktop boundaries
 */
export const clampPosition = (
  pos: WidgetDesktopPosition,
  size: { rows: number; cols: number },
  gridDim: GridDimensions
): WidgetDesktopPosition => ({
  row: Math.max(0, Math.min(pos.row, Math.max(0, gridDim.rows - size.rows))),
  col: Math.max(0, Math.min(pos.col, Math.max(0, gridDim.cols - size.cols))),
});

/**
 * Checks if a proposed item position overlaps with existing occupied cells
 */
export const isColliding = (
  pos: WidgetDesktopPosition,
  itemSize: { rows: number; cols: number },
  occupiedCells: Set<string>
): boolean => {
  for (let r = 0; r < itemSize.rows; r++) {
    for (let c = 0; c < itemSize.cols; c++) {
      if (occupiedCells.has(makeCellKey(pos.row + r, pos.col + c))) {
        return true;
      }
    }
  }
  return false;
};

/**
 * Clamps a single grid cell to the desktop boundaries
 */
export const clampCellToBounds = (
  pos: WidgetDesktopPosition,
  gridDim: GridDimensions
): WidgetDesktopPosition => clampPosition(pos, { rows: 1, cols: 1 }, gridDim);

/**
 * Scans for the first free cell (left-to-right, top-to-bottom).
 * When the visible grid is full, scanning continues in virtual rows
 * below the visible area so every icon still gets a unique cell.
 */
export const scanFirstFreeCell = (
  occupiedCells: Set<string>,
  gridDim: GridDimensions
): WidgetDesktopPosition => {
  for (let row = 0; row < gridDim.rows; row += 1) {
    for (let col = 0; col < gridDim.cols; col += 1) {
      if (!occupiedCells.has(makeCellKey(row, col))) {
        return { row, col };
      }
    }
  }

  let row = gridDim.rows;
  let col = 0;
  let guard = 0;
  while (occupiedCells.has(makeCellKey(row, col)) && guard < 10_000) {
    col += 1;
    if (col >= gridDim.cols) {
      col = 0;
      row += 1;
    }
    guard += 1;
  }
  return { row, col };
};

/**
 * Resolves the stored icon layout into collision-free display positions
 * for the current grid size — without mutating the stored layout.
 *
 * Stored positions are the user's intended layout and may lie outside the
 * current grid (e.g. after moving to a smaller display). Each visible icon
 * is clamped into bounds; if its cell is already taken, it is moved to the
 * nearest free cell for display only. Icons closer to the top-left keep
 * their cell, so the resolution is deterministic.
 */
export const resolveDisplayPositions = (
  storedPositions: Record<string, WidgetDesktopPosition>,
  visibleIds: string[],
  gridDim: GridDimensions,
  blockedCells: Set<string>
): Record<string, WidgetDesktopPosition> => {
  const resolved: Record<string, WidgetDesktopPosition> = {};
  const occupied = new Set<string>(blockedCells);

  const idsWithPosition = visibleIds
    .filter((id) => storedPositions[id])
    .sort((a, b) => {
      const posA = storedPositions[a];
      const posB = storedPositions[b];
      return posA.row - posB.row || posA.col - posB.col || (a < b ? -1 : 1);
    });

  for (const id of idsWithPosition) {
    const clamped = clampCellToBounds(storedPositions[id], gridDim);
    let target = clamped;
    if (occupied.has(makeCellKey(target.row, target.col))) {
      target =
        findNearestFreePosition(clamped, { rows: 1, cols: 1 }, gridDim, occupied) ??
        scanFirstFreeCell(occupied, gridDim);
    }
    resolved[id] = target;
    occupied.add(makeCellKey(target.row, target.col));
  }

  return resolved;
};

/**
 * Breadth-First Search to find the nearest available free position
 */
export const findNearestFreePosition = (
  target: WidgetDesktopPosition,
  itemSize: { rows: number; cols: number },
  gridDim: GridDimensions,
  occupiedCells: Set<string>
): WidgetDesktopPosition | null => {
  const start = clampPosition(target, itemSize, gridDim);
  if (!isColliding(start, itemSize, occupiedCells)) return start;

  const queue: WidgetDesktopPosition[] = [start];
  const visited = new Set<string>([makeCellKey(start.row, start.col)]);
  const directions = [
    { r: 0, c: 1 },
    { r: 1, c: 0 },
    { r: 0, c: -1 },
    { r: -1, c: 0 },
    { r: 1, c: 1 },
    { r: 1, c: -1 },
    { r: -1, c: 1 },
    { r: -1, c: -1 },
  ];

  while (queue.length > 0) {
    const current = queue.shift()!;

    for (const dir of directions) {
      const next = {
        row: current.row + dir.r,
        col: current.col + dir.c,
      };

      // Check boundaries
      if (
        next.row < 0 ||
        next.col < 0 ||
        next.row + itemSize.rows > gridDim.rows ||
        next.col + itemSize.cols > gridDim.cols
      )
        continue;

      const key = makeCellKey(next.row, next.col);
      if (visited.has(key)) continue;
      visited.add(key);

      if (!isColliding(next, itemSize, occupiedCells)) {
        return next;
      }

      queue.push(next);
    }

    // Guard against infinite loop in catastrophic cases
    if (visited.size > 1000) break;
  }

  return null;
};
